import React, { createContext, useContext } from 'react';
import type { FilterBarController, FilterBarLabels, FilterItemConfig, FilterSize } from '../types';
import { DEFAULT_FILTER_BAR_LABELS } from '../types';

/**
 * Internal context — NOT a public export.
 *
 * Plan Section "Recommended Approach":
 *   "Internal context, not an exported provider. The framework uses React.Context
 *    internally so deeply-nested children (each pill, popover contents, FilterSummary)
 *    can read shared state without prop drilling. This context is not a public export."
 */
export interface FilterBarContextValue {
  controller: FilterBarController;
  size: FilterSize;
  labels: Required<FilterBarLabels>;
  isReady: boolean;
  isLoadingExternal: boolean;
  itemsByName: Record<string, FilterItemConfig>;
}

/** Internal context — not a public export. Used by FilterItem, FilterSummary, useFilterField. */
export const FilterBarInternalContext = createContext<FilterBarContextValue | null>(null);

export const FilterBarContextProvider: React.FC<{
  value: FilterBarContextValue;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <FilterBarInternalContext.Provider value={value}>{children}</FilterBarInternalContext.Provider>
);

export function useFilterBarContext(): FilterBarContextValue {
  const ctx = useContext(FilterBarInternalContext);
  if (!ctx) {
    throw new Error(
      '[FilterBar] useFilterField / FilterItem must be rendered inside <FilterBar>',
    );
  }
  return ctx;
}

export { DEFAULT_FILTER_BAR_LABELS };
